import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  ControlContainer,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms'
import { ISelectItem } from '@lla-platform/core/core-ui'
import { environment } from '@lla-platform/core/core-util'
import { IShopLocationItem, ShopsState } from '@lla-platform/shops/shops-data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngxs/store'
import { Dictionary, uniq } from 'lodash'
import moment from 'moment'

export interface ITableHeaderSortInfo {
  columnName: string
  title?: string
}

@UntilDestroy()
@Component({
  selector: 'lla-shops-selector',
  templateUrl: './shops-selector.component.html',
  styleUrls: ['./shops-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ShopsSelectorComponent implements OnInit {
  @Input() controlName: string
  @Input() selectedGroups?: string[]
  @Input() selectedPartialGroups?: string[]
  @Input() searchInputId: string
  @Input() onlyUserShops = true
  @Input() numberOfColumns = 4
  @Input() hideStates = false
  @Input() minSelectable: number | undefined = undefined
  @Input() maxSelectable: number | undefined = undefined
  @Input() showLimitMessage = false

  formControl: UntypedFormControl
  statesList: string[] = []
  regionalManagersList: ISelectItem[] = []
  locationGroupsList: ISelectItem[] = []
  allShopsList: ISelectItem[] = []
  filterdShopList: ISelectItem[] = []
  Math = Math
  shopsGroupByStates: Dictionary<IShopLocationItem[]>
  numberOfShopsSelectedInEachState: Dictionary<number> = {}
  shopsGroupByManager: Dictionary<IShopLocationItem[]>
  numberOfShopsSelectedForEachManager: Dictionary<number> = {}
  districtManagersList: ISelectItem[] = []
  shopsGroupByDistrictManager: Dictionary<IShopLocationItem[]>
  numberOfShopsSelectedForEachDistrictManager: Dictionary<number> = {}
  shopsGroupByLocationGroup: Dictionary<IShopLocationItem[]>
  numberOfShopsSelectedForEachLocationGroup: Dictionary<number> = {}
  numberOfShopsOpenOnSaturday = 0
  numberOfShopsSelectedOpenOnSaturday = 0
  saturdayShopIds: string[] = []

  form: UntypedFormGroup
  allTakeOverTypeTypes: ISelectItem[] = [
    {
      label: 'Before',
      value: 'before'
    },
    {
      label: 'After',
      value: 'after'
    }
  ]
  showTakeOverFilter = false

  constructor(
    private store: Store,
    private controlContainer: ControlContainer,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.formControl = this.controlContainer.control?.get(this.controlName) as UntypedFormControl
    this.allShopsList = this.store.selectSnapshot<ISelectItem[]>(
      this.onlyUserShops ? ShopsState.userShopsAsList : ShopsState.allShopsAsList
    )
    this.statesList = this.store.selectSnapshot<string[]>(
      this.onlyUserShops ? ShopsState.userStates : ShopsState.allStates
    )
    this.regionalManagersList = this.store.selectSnapshot<ISelectItem[]>(
      this.onlyUserShops
        ? ShopsState.userRegionalManagersInLocations
        : ShopsState.allRegionalManagersInLocations
    )
    this.districtManagersList = this.store.selectSnapshot<ISelectItem[]>(
      this.onlyUserShops
        ? ShopsState.userDistrictManagersInLocations
        : ShopsState.allDistrictManagersInLocations
    )
    this.locationGroupsList = this.store.selectSnapshot<ISelectItem[]>(
      this.onlyUserShops
        ? ShopsState.userLocationGroupsInLocations
        : ShopsState.allLocationGroupsInLocations
    )

    this.shopsGroupByStates = this.onlyUserShops
      ? this.store.selectSnapshot(ShopsState.userShopsGroupByStates)
      : this.store.selectSnapshot(ShopsState.allShopsGroupByStates)

    this.shopsGroupByManager = this.onlyUserShops
      ? this.store.selectSnapshot(ShopsState.userShopsGroupByRegionalManager)
      : this.store.selectSnapshot(ShopsState.allShopsGroupByRegionalManager)

    this.shopsGroupByDistrictManager = this.onlyUserShops
      ? this.store.selectSnapshot(ShopsState.userShopsGroupByDistrictManager)
      : this.store.selectSnapshot(ShopsState.allShopsGroupByDistrictManager)

    this.shopsGroupByLocationGroup = this.onlyUserShops
      ? this.store.selectSnapshot(ShopsState.userShopsGroupByLocationGroup)
      : this.store.selectSnapshot(ShopsState.allShopsGroupByLocationGroup)

    this.calculateSaturdayShopsCount()
    this.setSelectedGroup()

    this.formControl?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.setSelectedGroup()
    })

    this.form = this.fb.group({
      searchText: [null],
      takeOverType: ['before'],
      takeOverDate: [null]
    })

    this.form?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.filterItems()
    })

    this.filterItems()
  }

  toggleSelectedGroup(
    item: string,
    type: 'state' | 'regionalManager' | 'districtManager' | 'locationGroup' = 'state'
  ) {
    const groupShops = this.getGroupMap(type)[item]?.map((el) => el.id) ?? []
    const currentShopIds: string[] = this.formControl?.value ?? []

    if (this.selectedGroups?.includes(item)) {
      this.formControl?.setValue(currentShopIds.filter((el) => !groupShops.includes(el)))
    } else {
      this.formControl?.setValue(uniq([...currentShopIds, ...groupShops]))
    }
  }

  setSelectedGroup() {
    const currentShopIds: string[] = this.formControl?.value ?? []
    this.selectedPartialGroups = []
    this.selectedGroups = []

    this.numberOfShopsSelectedInEachState = this.collectGroupStats(
      this.shopsGroupByStates,
      currentShopIds
    )
    this.numberOfShopsSelectedForEachManager = this.collectGroupStats(
      this.shopsGroupByManager,
      currentShopIds
    )
    this.numberOfShopsSelectedForEachDistrictManager = this.collectGroupStats(
      this.shopsGroupByDistrictManager,
      currentShopIds
    )
    this.numberOfShopsSelectedForEachLocationGroup = this.collectGroupStats(
      this.shopsGroupByLocationGroup,
      currentShopIds
    )

    // Check Saturday shops
    if (this.saturdayShopIds.length > 0) {
      this.numberOfShopsSelectedOpenOnSaturday = this.saturdayShopIds.filter((id) =>
        currentShopIds.includes(id)
      ).length
      if (this.saturdayShopIds.every((el) => currentShopIds.includes(el))) {
        this.selectedGroups?.push('saturday')
      }
      if (currentShopIds.find((el) => this.saturdayShopIds.includes(el))) {
        this.selectedPartialGroups?.push('saturday')
      }
    }
  }

  private getGroupMap(
    type: 'state' | 'regionalManager' | 'districtManager' | 'locationGroup'
  ): Dictionary<IShopLocationItem[]> {
    switch (type) {
      case 'state':
        return this.shopsGroupByStates
      case 'regionalManager':
        return this.shopsGroupByManager
      case 'districtManager':
        return this.shopsGroupByDistrictManager
      case 'locationGroup':
        return this.shopsGroupByLocationGroup
    }
  }

  private collectGroupStats(
    groupMap: Dictionary<IShopLocationItem[]>,
    currentShopIds: string[]
  ): Dictionary<number> {
    const counts: Dictionary<number> = {}
    Object.keys(groupMap).forEach((key) => {
      const shops = groupMap[key] ?? []
      const ids = shops.map((el) => el.id)
      if (ids.length > 0 && ids.every((id) => currentShopIds.includes(id))) {
        this.selectedGroups?.push(key)
      }
      if (currentShopIds.find((id) => ids.includes(id))) {
        this.selectedPartialGroups?.push(key)
      }
      counts[key] = shops.filter((el) => currentShopIds.includes(el.id)).length
    })
    return counts
  }

  filterItems() {
    let filteredList = [...this.allShopsList]

    // Apply search text filter
    const trimValue = this.form.get('searchText')?.value?.toLowerCase().trim()
    if (trimValue) {
      filteredList = filteredList.filter((el) => el.label.toLowerCase().includes(trimValue))
    }

    // Apply takeOverDate filter
    const takeOverType = this.form.get('takeOverType')?.value
    const takeOverDate = this.form.get('takeOverDate')?.value
    if (takeOverDate && takeOverType) {
      const selectedDate = moment(takeOverDate)
      filteredList = filteredList.filter((el) => {
        if (!el.extraInfo.takeOverDate) {
          return false
        }
        const shopTakeOverDate = moment(el.extraInfo.takeOverDate)

        if (takeOverType === 'before') {
          return shopTakeOverDate.isSameOrBefore(selectedDate, 'day')
        } else if (takeOverType === 'after') {
          return shopTakeOverDate.isSameOrAfter(selectedDate, 'day')
        }
        return true
      })
    }

    this.filterdShopList = filteredList
  }

  allFilterdShopsSelected() {
    const locationIds = this.formControl?.value
    return locationIds && locationIds.length > 0 && locationIds.length === this.filterdShopList.length
  }

  selectAllFilterdShops() {
    this.selectedGroups = []
    this.selectedPartialGroups = []
    const locationIds = this.formControl?.value
    if (locationIds && locationIds.length === this.filterdShopList.length) {
      this.formControl?.reset()
      return
    }
    this.formControl?.setValue(this.filterdShopList.map((el) => el.value))
  }

  isItemDisabled(item: ISelectItem) {
    if (this.minSelectable === undefined || this.maxSelectable === undefined) {
      return false
    }

    if (
      this.formControl?.value?.includes(item.value) &&
      this.formControl?.value?.length <= this.minSelectable
    ) {
      return true
    }

    if (
      !this.formControl?.value?.includes(item.value) &&
      this.formControl?.value?.length >= this.maxSelectable
    ) {
      return true
    }

    return false
  }

  changeTakeOverStatus() {
    this.showTakeOverFilter = !this.showTakeOverFilter

    if (!this.showTakeOverFilter) {
      this.resetTakeOverFilters()
    }
  }

  resetTakeOverFilters() {
    this.form.patchValue({
      takeOverType: 'before',
      takeOverDate: null
    })
  }

  calculateSaturdayShopsCount() {
    const saturdayShops = this.allShopsList.filter((shop) => shop.extraInfo?.isSaturdayOpen === true)
    this.numberOfShopsOpenOnSaturday = saturdayShops.length
    this.saturdayShopIds = saturdayShops.map((shop) => shop.value)
  }

  toggleSaturdaySelection() {
    const currentShopIds: string[] = this.formControl?.value ?? []

    if (this.selectedGroups?.includes('saturday')) {
      this.formControl?.setValue(currentShopIds.filter((el) => !this.saturdayShopIds.includes(el)))
    } else {
      this.formControl?.setValue(uniq([...currentShopIds, ...this.saturdayShopIds]))
    }
  }
}
